import React, { useState, useEffect } from 'react';
import ClientIrccStudy, { isStudyProcedure } from './ClientIrccStudyDocuments';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/ClientDetail.css';
import clientsAPI from '../services/clientsAPI';
import DocumentUploader from './DocumentUploader';
import { getAllFactures, getFactureById, formatMontant, formatDate as formatFactureDate } from '../services/facturesAPI';
import { openFacturePdfInNewTab } from '../utils/pdf/facturePdf';
import { buildApiUrl } from '../services/apiBase';

console.log('AFFICHAGE : ClientDetail.js');

function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);
  const [activeTab, setActiveTab] = useState('informations');
  const [userRole, setUserRole] = useState('conseillere'); // À remplacer par le rôle réel de l'utilisateur connecté
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  // Paiements
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [invoiceQuery, setInvoiceQuery] = useState('');
  const [invoiceSort, setInvoiceSort] = useState({ key: 'dateEmission', dir: 'desc' });
  const [invoicePage, setInvoicePage] = useState(1);
  const invoicesPerPage = 10;
  const [invoiceTotalPaid, setInvoiceTotalPaid] = useState(0);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('tous');
  const [factureLoading, setFactureLoading] = useState(false);

  const openInvoicePdf = async (inv) => {
    setFactureLoading(true);
    try {
      const response = await getFactureById(inv.id);
      if (!response?.success || !response.data) {
        throw new Error('Facture introuvable');
      }
      await openFacturePdfInNewTab(response.data);
    } catch (error) {
      alert(error.message || 'Impossible de charger la facture');
    } finally {
      setFactureLoading(false);
    }
  };

  // Charger les détails du client depuis l'API
  useEffect(() => {
    const loadClientDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log(`🔄 Chargement des détails du client ${id}...`);
        
        const response = await clientsAPI.getClientById(id);
        console.log('✅ Détails du client récupérés:', response);
        
        if (response.success && response.data) {
          setClient(response.data);
          console.log(`📊 Client ${response.data.nom} ${response.data.prenom} chargé avec succès`);
        } else {
          console.log('⚠️ Client non trouvé ou format de réponse inattendu');
          setError('Client non trouvé');
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement du client:', error);
        setError('Erreur lors du chargement des détails du client. Vérifiez que le serveur backend est démarré.');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      loadClientDetails();
    }
  }, [id]);

  // Charger les factures payées de ce client depuis le module Facturation
  useEffect(() => {
    const fetchPaidInvoices = async () => {
      try {
        const res = await getAllFactures();
        const clientId = parseInt(id);
        const filtered = (res.data || [])
          .filter(f => (f.client_id === clientId || f.clientId === clientId))
          .map(f => ({
            ...f,
            id: f.id,
            numero: f.numero || f.numero_facture || f.numeroFacture,
            dateEmission: f.dateEmission || f.date_emission || f.date,
            datePaiement: f.datePaiement || f.date_paiement || null,
            methodePaiement: f.methodePaiement || f.mode_paiement,
            montant: f.montant_ttc || f.montant || 0,
            monnaie: f.monnaie || 'MAD',
            reference: f.psp_payment_id || f.reference || f.reference_transaction,
            pdfUrl: f.pdf_url || f.url_pdf || null,
            statut: (f.statut || '').toLowerCase()
          }));

        setPaidInvoices(filtered);
        const total = filtered
          .filter((it) => it.statut === 'payee')
          .reduce((sum, it) => sum + (parseFloat(it.montant) || 0), 0);
        setInvoiceTotalPaid(total);
        setInvoicePage(1);
      } catch (e) {
        console.warn('Impossible de charger les factures payées:', e?.message);
        setPaidInvoices([]);
        setInvoiceTotalPaid(0);
      }
    };
    if (id) fetchPaidInvoices();
  }, [id]);

  // Fonction pour formater les dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Fonction pour ouvrir la prévisualisation d'un document
  const handleViewDocument = (document) => {
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  // Fonction pour télécharger un document
  const handleDownloadDocument = (document) => {
    // Créer un lien de téléchargement
    const link = document.createElement('a');
    link.href = document.url || document.path;
    link.download = document.nom || 'document.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fonction pour obtenir l'icône selon le type de document
  const getDocumentIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf':
        return 'fas fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fas fa-file-word';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'fas fa-file-image';
      default:
        return 'fas fa-file';
    }
  };

  // Utils invoices (tri/recherche/pagination)
  const filteredSortedInvoices = () => {
    const q = invoiceQuery.trim().toLowerCase();
    const list = paidInvoices.filter(it => {
      const matchQuery = !q || (it.numero && String(it.numero).toLowerCase().includes(q)) || (it.reference && String(it.reference).toLowerCase().includes(q)) || (it.methodePaiement && String(it.methodePaiement).toLowerCase().includes(q));
      const matchStatus = invoiceStatusFilter === 'tous' || (it.statut && it.statut.toLowerCase() === invoiceStatusFilter);
      return matchQuery && matchStatus;
    });
    const dirFactor = invoiceSort.dir === 'asc' ? 1 : -1;
    return list.sort((a,b)=>{
      const k = invoiceSort.key;
      const av = a[k] ?? '';
      const bv = b[k] ?? '';
      if (av < bv) return -1*dirFactor;
      if (av > bv) return 1*dirFactor;
      return 0;
    });
  };

  const paginate = (arr) => {
    const start = (invoicePage - 1) * invoicesPerPage;
    return arr.slice(start, start + invoicesPerPage);
  };

  const getTotalInvoicePages = () => {
    const total = filteredSortedInvoices().length;
    return Math.max(1, Math.ceil(total / invoicesPerPage));
  };

  const toggleInvoiceSort = (key) => {
    setInvoiceSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  // Fonction pour obtenir la classe CSS selon le type de document
  const getDocumentTypeClass = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf':
        return 'document-pdf';
      case 'doc':
      case 'docx':
        return 'document-word';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'document-image';
      default:
        return 'document-default';
    }
  };

  // Fonction pour obtenir la liste des documents requis selon le type de procédure
  const getRequiredDocuments = () => {
    const baseDocuments = [
      'Copie de CIN',
      'Copie du passeport',
      'Photo d\'identité'
    ];

    switch (client?.type_procedure) {
      case 'Visa visiteur':
      case 'Visa Visiteur':
        return [
          ...baseDocuments,
          'Invitation (si applicable)',
          'Dossier financier',
          'Dossier emploi'
        ];
      case 'Permis de travail':
        return [
          ...baseDocuments,
          'Offre d\'emploi',
          'EIMT',
          'Diplômes',
          'CV'
        ];
      case 'Permis d\'études':
        return [
          ...baseDocuments,
          'Lettre d\'admission',
          'Preuve financière'
        ];
      case 'Résidence permanente':
        return [
          ...baseDocuments,
          'Certificat de police',
          'Examen médical',
          'Preuves d\'expérience',
          'Tests de langue'
        ];
      default:
        return baseDocuments;
    }
  };

  // Fonction pour gérer l'upload d'un fichier
  const handleFileUpload = async (documentName, file) => {
    if (!file) return;

    // Vérifier la taille du fichier (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximum: 10MB');
      return;
    }

    // Vérifier le type de fichier
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      alert('Type de fichier non autorisé. Formats acceptés: PDF, JPG, PNG, DOC, DOCX');
      return;
    }

    // Simuler l'upload du document
    const newDocument = {
      id: Date.now(),
      nom: documentName,
      fichier: file.name,
      type: file.type,
      statut: 'fourni',
      dateUpload: new Date().toISOString(),
      taille: file.size
    };

    // Mettre à jour la liste des documents
    const updatedDocuments = [...(client.documents || []), newDocument];
    setClient({ ...client, documents: updatedDocuments });

    console.log('Document téléversé:', newDocument);
    alert(`Document "${documentName}" téléversé avec succès !`);
  };

  // Fonction pour supprimer un document
  const handleRemoveDocument = (documentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      const updatedDocuments = client.documents.filter(doc => doc.id !== documentId);
      setClient({ ...client, documents: updatedDocuments });
      console.log('Document supprimé:', documentId);
    }
  };

  if (loading) {
    return (
      <div className="client-detail-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des détails du client...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-detail-container">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={() => navigate('/clients')} className="btn btn-secondary">
            Retour aux clients
          </button>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="client-detail-container">
        <div className="error-container">
          <p className="error-message">Client non trouvé</p>
          <button onClick={() => navigate('/clients')} className="btn btn-secondary">
            Retour aux clients
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="client-detail-container">
      {/* En-tête du client */}
      <div className="client-header">
        <div className="client-info">
          <h1>{client.prenom} {client.nom}</h1>
          <div className="client-meta">
            <span className="badge badge-procedure">{client.type_procedure}</span>
            <span className={`badge badge-statut statut-${client.statut.toLowerCase().replace(/\s+/g, '-')}`}>
              {client.statut}
            </span>
            {client.urgence && <span className="badge badge-urgent">Urgent</span>}
          </div>
        </div>
        <div className="client-actions">
          <button onClick={() => navigate('/clients')} className="btn btn-secondary">
            <i className="fas fa-arrow-left"></i> Retour
          </button>
          <button className="btn btn-primary">
            <i className="fas fa-edit"></i> Modifier
          </button>
        </div>
      </div>

      {/* Navigation par onglets */}
      <div className="tabs-navigation">
        {isStudyProcedure(client) && <button type="button" className={`tab-button ${activeTab === 'ircc' ? 'active' : ''}`} onClick={() => setActiveTab('ircc')}>IRCC — Permis d’études</button>}
        <button 
          className={`tab-button ${activeTab === 'informations' ? 'active' : ''}`}
          onClick={() => setActiveTab('informations')}
        >
          <i className="fas fa-user"></i> Informations personnelles
        </button>
        <button 
          className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          <i className="fas fa-phone"></i> Contact
        </button>
        <button 
          className={`tab-button ${activeTab === 'procedure' ? 'active' : ''}`}
          onClick={() => setActiveTab('procedure')}
        >
          <i className="fas fa-file-alt"></i> Procédure
        </button>
        <button 
          className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <i className="fas fa-folder"></i> Documents
        </button>
        <button 
          className={`tab-button ${activeTab === 'paiements' ? 'active' : ''}`}
          onClick={() => setActiveTab('paiements')}
        >
          <i className="fas fa-receipt"></i> Paiements
        </button>
        <button 
          className={`tab-button ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          <i className="fas fa-sticky-note"></i> Notes
        </button>
      </div>

      {/* Contenu des onglets */}
      <div className="tab-content">
        {activeTab === 'ircc' && isStudyProcedure(client) && <ClientIrccStudy key={client.id} client={client} />}
        {activeTab === 'informations' && (
          <div className="tab-pane">
            <h3>Informations personnelles</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Nom complet:</label>
                <span>{client.prenom} {client.nom}</span>
              </div>
              <div className="info-item">
                <label>Date de naissance:</label>
                <span>{formatDate(client.date_naissance)}</span>
              </div>
              <div className="info-item">
                <label>Email:</label>
                <span>{client.email}</span>
              </div>
              <div className="info-item">
                <label>Téléphone:</label>
                <span>{client.telephone}</span>
              </div>
              <div className="info-item">
                <label>Adresse complète:</label>
                <span className="address-text">{client.adresse || 'Non renseignée'}</span>
              </div>
              <div className="info-item">
                <label>Nationalité:</label>
                <span>{client.nationalite || 'Non renseignée'}</span>
              </div>
              <div className="info-item">
                <label>Numéro de dossier:</label>
                <span className="dossier-number">{client.numero_dossier}</span>
              </div>
              <div className="info-item">
                <label>Type de procédure:</label>
                <span className="procedure-type">{client.type_procedure}</span>
              </div>
              <div className="info-item">
                <label>Statut actuel:</label>
                <span className={`statut-badge statut-${client.statut.toLowerCase().replace(/\s+/g, '-')}`}>
                  {client.statut}
                </span>
              </div>
              <div className="info-item">
                <label>Conseillère assignée:</label>
                <span>{client.conseillere || 'Non assignée'}</span>
              </div>
              <div className="info-item">
                <label>Priorité:</label>
                <span className={client.urgence ? 'urgent' : 'normal'}>
                  {client.urgence ? '🔴 Urgent' : '🟢 Normal'}
                </span>
              </div>
              <div className="info-item">
                <label>Login client:</label>
                <span>{client.login_client || 'Non créé'}</span>
              </div>
              <div className="info-item">
                <label>Date de création:</label>
                <span>{formatDate(client.date_creation)}</span>
              </div>
              <div className="info-item">
                <label>Dernière modification:</label>
                <span>{formatDate(client.date_modification)}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="tab-pane">
            <h3>Informations de contact</h3>
            <div className="contact-sections">
              <div className="contact-section">
                <h4>Contact principal</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Email:</label>
                    <span>{client.email}</span>
                  </div>
                  <div className="info-item">
                    <label>Téléphone:</label>
                    <span>{client.telephone}</span>
                  </div>
                  <div className="info-item">
                    <label>Adresse:</label>
                    <span>{client.adresse || 'Non renseignée'}</span>
                  </div>
                </div>
              </div>

              <div className="contact-section">
                <h4>Contact alternatif</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Nom:</label>
                    <span>{client.contact_nom || 'Non renseigné'}</span>
                  </div>
                  <div className="info-item">
                    <label>Prénom:</label>
                    <span>{client.contact_prenom || 'Non renseigné'}</span>
                  </div>
                  <div className="info-item">
                    <label>Relation:</label>
                    <span>{client.contact_relation || 'Non renseignée'}</span>
                  </div>
                  <div className="info-item">
                    <label>Téléphone:</label>
                    <span>{client.contact_telephone || 'Non renseigné'}</span>
                  </div>
                  <div className="info-item">
                    <label>Email:</label>
                    <span>{client.contact_email || 'Non renseigné'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'procedure' && (
          <div className="tab-pane">
            <h3>Informations sur la procédure</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Type de procédure:</label>
                <span>{client.type_procedure}</span>
              </div>
              <div className="info-item">
                <label>Statut:</label>
                <span className={`statut-badge statut-${client.statut.toLowerCase().replace(/\s+/g, '-')}`}>
                  {client.statut}
                </span>
              </div>
              <div className="info-item">
                <label>Conseillère assignée:</label>
                <span>{client.conseillere}</span>
              </div>
              <div className="info-item">
                <label>Urgence:</label>
                <span className={client.urgence ? 'urgent' : 'normal'}>
                  {client.urgence ? 'Urgent' : 'Normal'}
                </span>
              </div>
              <div className="info-item">
                <label>Login client:</label>
                <span>{client.login_client || 'Non créé'}</span>
              </div>
              <div className="info-item">
                <label>Date de création:</label>
                <span>{formatDate(client.date_creation)}</span>
              </div>
              <div className="info-item">
                <label>Dernière modification:</label>
                <span>{formatDate(client.date_modification)}</span>
              </div>
            </div>
              {client.informations_specifiques && (
              <div className="specific-info">
                <h4>Informations spécifiques</h4>
                {(() => {
                  try {
                    const infos = typeof client.informations_specifiques === 'string' 
                      ? JSON.parse(client.informations_specifiques) 
                      : client.informations_specifiques;
                    
                    return (
                      <div className="info-grid">
                        {Object.entries(infos).map(([key, value]) => {
                          // Formatage des clés pour un affichage plus lisible
                          const formatKey = (key) => {
                            const keyMappings = {
                              'fondsDisponibles': 'Fonds disponibles',
                              'situationFamiliale': 'Situation familiale',
                              'dateVoyage': 'Date de voyage prévue',
                              'emploi': 'Situation d\'emploi',
                              'invitation': 'Lettre d\'invitation',
                              'lienParenteInvitant': 'Lien de parenté avec l\'invitant',
                              'niveauEtudes': 'Niveau d\'études',
                              'institutionProposee': 'Institution proposée',
                              'programmeEtudes': 'Programme d\'études',
                              'dureeEtudes': 'Durée des études',
                              'coutEtudes': 'Coût des études',
                              'preuvesFinancieres': 'Preuves financières',
                              'offreEmploi': 'Offre d\'emploi',
                              'employeur': 'Employeur',
                              'posteOffert': 'Poste offert',
                              'salaireOffert': 'Salaire offert',
                              'lieuTravail': 'Lieu de travail',
                              'experienceProfessionnelle': 'Expérience professionnelle',
                              'competencesLinguistiques': 'Compétences linguistiques',
                              'familleCanada': 'Famille au Canada',
                              'parentsCanada': 'Parents au Canada',
                              'relationFamiliale': 'Relation familiale',
                              'documentsSoutien': 'Documents de soutien',
                              'categorieRP': 'Catégorie de résidence permanente',
                              'scoreEEE': 'Score Entrée Express',
                              'experienceCanadienne': 'Expérience canadienne',
                              'certificationProfessionnelle': 'Certification professionnelle'
                            };
                            return keyMappings[key] || key.charAt(0).toUpperCase() + key.slice(1);
                          };

                          // Formatage des valeurs
                          const formatValue = (value) => {
                            if (value === 'oui' || value === 'yes') return '✅ Oui';
                            if (value === 'non' || value === 'no') return '❌ Non';
                            if (key === 'dateVoyage' && value) {
                              return formatDate(value);
                            }
                            if (key === 'fondsDisponibles' && value) {
                              return `${value} $`;
                            }
                            return value || 'Non renseigné';
                          };

                          return (
                            <div key={key} className="info-item">
                              <label>{formatKey(key)}:</label>
                              <span>{formatValue(value)}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  } catch (error) {
                    console.error('Erreur parsing informations spécifiques:', error);
                    return (
                      <div className="info-text">
                        <pre>{client.informations_specifiques}</pre>
                      </div>
                    );
                  }
                })()}
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="tab-pane">
            <h3>Documents</h3>
            <div className="documents-section">
              <div className="documents-table-container">
                <table className="documents-table">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>État</th>
                      <th>Fichier</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getRequiredDocuments().map((documentName, index) => {
                      const existingDoc = client.documents?.find(doc => doc.nom === documentName);
                      const isProvided = existingDoc && existingDoc.statut === 'fourni';
                      
                      return (
                        <tr key={index} className={isProvided ? 'document-provided' : 'document-pending'}>
                          <td className="document-name-cell">
                            <span className="document-name">{documentName}</span>
                          </td>
                          <td>
                            <span className={`status-badge ${isProvided ? 'status-fourni' : 'status-a-fournir'}`}>
                              {isProvided ? 'FOURNI' : 'À FOURNIR'}
                            </span>
                          </td>
                          <td className="file-upload-cell">
                            {isProvided ? (
                              <div className="file-info">
                                <i className="fas fa-file-pdf text-success"></i>
                                <span className="file-name">{existingDoc.fichier || 'Document.pdf'}</span>
                                {existingDoc.dateUpload && (
                                  <small className="upload-date">
                                    ({formatDate(existingDoc.dateUpload)})
                                  </small>
                                )}
                              </div>
                            ) : (
                              <div className="file-upload-wrapper">
                                <input
                                  type="file"
                                  id={`file-${index}`}
                                  className="file-upload-input"
                                  onChange={(e) => handleFileUpload(documentName, e.target.files[0])}
                                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                />
                                <label htmlFor={`file-${index}`} className="file-upload-label">
                                  <i className="fas fa-upload"></i> Choisir un fichier
                                </label>
                              </div>
                            )}
                          </td>
                          <td className="actions-cell">
                            {isProvided ? (
                              <>
                                <button 
                                  className="btn-icon"
                                  onClick={() => handleViewDocument(existingDoc)}
                                  title="Voir"
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                                <button 
                                  className="btn-icon"
                                  onClick={() => handleDownloadDocument(existingDoc)}
                                  title="Télécharger"
                                >
                                  <i className="fas fa-download"></i>
                                </button>
                                <button 
                                  className="btn-icon btn-danger"
                                  onClick={() => handleRemoveDocument(existingDoc.id)}
                                  title="Supprimer"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </>
                            ) : (
                              <span className="no-actions">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'paiements' && (
          <div className="tab-pane">
            <h3>Paiements</h3>

            {/* Section Factures payées */}
            <div className="paid-invoices-section">
              <div className="section-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <i className="fas fa-check-circle" style={{color:'#10b981'}}></i>
                  <h4 style={{margin:0}}>Factures du client</h4>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                  <input
                    type="text"
                    value={invoiceQuery}
                    onChange={(e)=>{setInvoiceQuery(e.target.value); setInvoicePage(1);} }
                    placeholder="Rechercher (n° facture, référence, mode)..."
                    style={{padding:'8px 10px',border:'1px solid #dee2e6',borderRadius:6,minWidth:260}}
                  />
                  <select value={invoiceStatusFilter} onChange={(e)=>{setInvoiceStatusFilter(e.target.value); setInvoicePage(1);}} style={{padding:'8px 10px',border:'1px solid #dee2e6',borderRadius:6}}>
                    <option value="tous">Tous les statuts</option>
                    <option value="brouillon">Brouillon</option>
                    <option value="payable">Payable</option>
                    <option value="payee">Payée</option>
                    <option value="annulee">Annulée</option>
                  </select>
                  <div style={{fontSize:13,color:'#6c757d'}}>
                    Total payé: <strong>{formatMontant(invoiceTotalPaid, (paidInvoices[0]?.monnaie)||'MAD')}</strong>
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      // Export CSV simple côté client
                      const rows = [['Numero','Date facture','Date paiement','Mode paiement','Montant TTC','Devise','Référence']];
                      filteredSortedInvoices().forEach(it=>{
                        rows.push([it.numero, formatFactureDate(it.dateEmission), formatFactureDate(it.datePaiement), it.methodePaiement||'', it.montant, it.monnaie, it.reference||'']);
                      });
                      const csv = rows.map(r=>r.map(v=>`"${(v??'').toString().replace(/"/g,'""')}"`).join(',')).join('\n');
                      const blob = new Blob([csv], {type:'text/csv;charset=utf-8;'});
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href=url; a.download=`factures-payees-client-${id}.csv`; a.click(); URL.revokeObjectURL(url);
                    }}
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              <div className="invoices-table-container">
                <table className="invoices-table">
                  <thead>
                    <tr>
                      <th onClick={()=>toggleInvoiceSort('numero')}>N° de facture</th>
                      <th onClick={()=>toggleInvoiceSort('dateEmission')}>Date de facture</th>
                      <th onClick={()=>toggleInvoiceSort('datePaiement')}>Date/heure de paiement</th>
                      <th onClick={()=>toggleInvoiceSort('methodePaiement')}>Mode de paiement</th>
                      <th onClick={()=>toggleInvoiceSort('montant')}>Montant TTC</th>
                      <th>Devise</th>
                      <th onClick={()=>toggleInvoiceSort('reference')}>Référence</th>
                      <th onClick={()=>toggleInvoiceSort('statut')}>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginate(filteredSortedInvoices()).map(inv => (
                      <tr key={inv.id} className="invoice-row" onClick={() => openInvoicePdf(inv)} style={{cursor:'pointer'}}>
                        <td>{inv.numero || 'Non attribu\u00e9'}</td>
                        <td>{formatFactureDate(inv.dateEmission)}</td>
                        <td>{formatFactureDate(inv.datePaiement)}</td>
                        <td>{inv.methodePaiement || '-'}</td>
                        <td>{formatMontant(inv.montant, inv.monnaie)}</td>
                        <td>{inv.monnaie}</td>
                        <td>{inv.reference || '-'}</td>
                        <td>{(inv.statut||'').toUpperCase()}</td>
                        <td>
                          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                            <button className="btn btn-secondary" onClick={(e)=>{e.stopPropagation(); openInvoicePdf(inv);}}>Voir</button>
                            {inv.pdfUrl ? (
                              <button className="btn btn-secondary" onClick={(e)=>{e.stopPropagation(); window.open(inv.pdfUrl,'_blank');}}>PDF</button>
                            ) : (
                              <span className="text-muted">PDF -</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredSortedInvoices().length === 0 && (
                      <tr><td colSpan="9" style={{textAlign:'center',color:'#6c757d',padding:'16px'}}>Aucune facture</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {getTotalInvoicePages() > 1 && (
                <div style={{display:'flex',justifyContent:'center',gap:8,marginTop:12}}>
                  <button className="btn btn-secondary" disabled={invoicePage===1} onClick={()=>setInvoicePage(p=>Math.max(1,p-1))}> ← </button>
                  <span style={{alignSelf:'center',fontSize:13,color:'#6c757d'}}>Page {invoicePage} / {getTotalInvoicePages()}</span>
                  <button className="btn btn-secondary" disabled={invoicePage===getTotalInvoicePages()} onClick={()=>setInvoicePage(p=>Math.min(getTotalInvoicePages(),p+1))}> → </button>
                </div>
              )}
            </div>

            {/* Section Contrat du client */}
            <div className="contracts-section" style={{marginTop:24}}>
              <div className="section-header" style={{display:'flex',alignItems:'center',gap:8}}>
                <i className="fas fa-file-contract" style={{color:'#2563eb'}}></i>
                <h4 style={{margin:0}}>Contrat du client</h4>
              </div>
              <p className="text-muted" style={{marginTop:6}}>Stockage sous Dossier Contrats du référentiel Documents, lié au client_id.</p>
              <DocumentUploader
                title="Ajouter un contrat"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                maxSizeMB={20}
                folder={`Contrats/${id}`}
                onUploadSuccess={(fileInfo)=>{
                  console.log('Contrat uploadé', fileInfo);
                }}
                enablePreview
                enableReplace
                enableDelete={userRole==='administrateur' || userRole==='directeur'}
              />
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="tab-pane">
            <h3>Notes</h3>
            <div className="notes-section">
              {/* Liste des notes existantes */}
              <div className="notes-list">
                {(() => {
                  let notes = [];
                  try {
                    if (Array.isArray(client.notes)) notes = client.notes;
                    else if (typeof client.notes === 'string' && client.notes.trim()) notes = JSON.parse(client.notes);
                  } catch (e) { notes = []; }
                  if (!notes.length) {
                    return <p className="text-muted">Aucune note pour le moment.</p>;
                  }
                  return notes.map((n, idx) => (
                    <div key={n.id || idx} className="note-item">
                      <div style={{fontSize:12,color:'#6c757d'}}>{n.date || ''}</div>
                      <div style={{margin:'6px 0'}}>{n.texte || n.content}</div>
                      <div style={{fontSize:12,color:'#9aa0a6'}}>{n.auteur || 'Utilisateur'}</div>
                    </div>
                  ));
                })()}
              </div>

              {/* Saisie d'une nouvelle note */}
              <div className="note-add-form" style={{marginTop:16}}>
                <textarea id="new-note-text" rows="3" placeholder="Saisir une nouvelle note..." style={{width:'100%',padding:'10px',border:'1px solid #dee2e6',borderRadius:6}} />
                <button
                  className="btn btn-primary"
                  style={{marginTop:8}}
                  onClick={async ()=>{
                    const el = document.getElementById('new-note-text');
                    const texte = (el && el.value || '').trim();
                    if (!texte) return;
                    try {
                      const resp = await fetch(buildApiUrl(`/clients/${id}/notes`), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')||''}` },
                        body: JSON.stringify({ note: { texte, auteur: localStorage.getItem('userName')||'Utilisateur', date: new Date().toISOString() } })
                      });
                      if (resp.ok) {
                        // Recharger le client pour afficher la note
                        const fresh = await clientsAPI.getClientById(id);
                        if (fresh.success && fresh.data) setClient(fresh.data);
                        if (el) el.value='';
                      }
                    } catch (e) {
                      console.warn('Erreur ajout note', e.message);
                    }
                  }}
                >
                  <i className="fas fa-plus"></i> Ajouter une note
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modale de prévisualisation de document */}
      {showDocumentModal && (
        <div className="document-modal">
          <div className="modal-content">
            <span className="close" onClick={() => setShowDocumentModal(false)}>&times;</span>
            {selectedDocument && (
              <div className="document-preview">
                <h3>Prévisualisation du document</h3>
                <div className="document-details">
                  <i className={getDocumentIcon(selectedDocument.type)}></i>
                  <div className="document-info">
                    <span className="document-name">{selectedDocument.nom}</span>
                    <span className="document-type">{selectedDocument.type}</span>
                  </div>
                </div>
                <div className="document-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleDownloadDocument(selectedDocument)}
                  >
                    <i className="fas fa-download"></i> Télécharger
                  </button>
                </div>
                <div className="document-viewer">
                  {/* Intégrer un visualiseur de document ici, par exemple un iframe pour les PDF */}
                  {selectedDocument.type.toLowerCase() === 'pdf' ? (
                    <iframe 
                      src={selectedDocument.url || selectedDocument.path} 
                      title="Document PDF"
                      className="pdf-viewer"
                    ></iframe>
                  ) : (
                    <p>Impossible de prévisualiser ce type de document.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientDetail;
